import { useEffect, useState } from 'react';
export const OPERATORS = ['+', '-', '×', ':'];
export const OPERATOR_SYMBOLS = {
  'PLUS': '+',
  'MINUS': '-',
  'MULTIPLY': '×',
  'DIVIDE': ':',
};

export const useCalculator = (initialValue: string = '', onTypingStart?: () => void, selectOnFocus: boolean = false) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [operation, setOperation] = useState<string | null>(null);
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [expression, setExpression] = useState<string>('');
  const [shouldReplaceOnNextInput, setShouldReplaceOnNextInput] = useState(false);
  const [isValueSelected, setIsValueSelected] = useState(false); // For visual "selected" state

  // Call this when keyboard opens to enable "select all" behavior
  const markForReplacement = () => {
    if (selectOnFocus) {
      setShouldReplaceOnNextInput(true);
      setIsValueSelected(true); // Show visual selection
    }
  };

  // Clear visual selection state
  const clearSelection = () => {
    setShouldReplaceOnNextInput(false);
    setIsValueSelected(false);
  };

  const calculate = (firstValue: number, secondValue: number, operation: string): number => {
    switch (operation) {
      case OPERATOR_SYMBOLS.PLUS: return firstValue + secondValue;
      case OPERATOR_SYMBOLS.MINUS: return firstValue - secondValue;
      case OPERATOR_SYMBOLS.MULTIPLY: return firstValue * secondValue;
      case OPERATOR_SYMBOLS.DIVIDE: return secondValue !== 0 ? firstValue / secondValue : firstValue;
      default: return secondValue;
    }
  };

  const evaluateExpression = (expr: string): number => {
    // Remove any trailing spaces and operators
    const cleanExpr = expr.trim().replace(/\s*[+\-×:]\s*$/, '');
    
    // Find the most recent complete operation (number operator number)
    const operatorMatch = cleanExpr.match(/(.+?)\s*([+\-×:])\s*(.+)$/);
    if (operatorMatch) {
      const [, firstNum, operator, secondNum] = operatorMatch;
      const first = parseFloat(firstNum.trim()) || 0;
      const second = parseFloat(secondNum.trim()) || 0;
      return calculate(first, second, operator);
    }
    return parseFloat(cleanExpr) || 0;
  };

  const handleNumberPress = (num: string, onFieldChange?: (value: string) => void): void => {
    // Notify that typing has started
    if (onTypingStart) {
      onTypingStart();
    }

    setInputValue(prevInputValue => {
      let newValue;

      // If marked for replacement (selectOnFocus), replace entire value with new number
      if (shouldReplaceOnNextInput) {
        clearSelection();
        newValue = num;
        setExpression(newValue);
        setPreviousValue(null);
        setOperation(null);
        if (onFieldChange) {
          onFieldChange(newValue);
        }
        return newValue;
      }

      // Check if we're starting a new number after an operator
      if (prevInputValue.endsWith(' ')) {
        newValue = prevInputValue + num;
      } else if (prevInputValue === '0' || prevInputValue === '') {
        newValue = num;
      } else if (prevInputValue === '-') {
        newValue = '-' + num;
      } else {
        newValue = prevInputValue + num;
      }

      if (onFieldChange) {
        onFieldChange(newValue);
      }

      // Allow longer expressions since they include operators
      if (newValue.length <= 50) {
        setExpression(newValue);
        return newValue;
      }

      return prevInputValue;
    });
  };

  const handleDecimal = (): void => {
    // Notify that typing has started
    if (onTypingStart) {
      onTypingStart();
    }

    setInputValue(prevInputValue => {
      let newValue;

      if (prevInputValue === '' || prevInputValue === '0') {
        newValue = '0.';
      } else if (prevInputValue.endsWith(' ')) {
        // Starting a new decimal number after an operator
        newValue = prevInputValue + '0.';
      } else {
        // Check if the current number part already has a decimal
        const parts = prevInputValue.split(/[+\-×:]/);
        const currentPart = parts[parts.length - 1].trim();

        if (currentPart.indexOf('.') === -1) {
          newValue = prevInputValue + '.';
        } else {
          return prevInputValue; // Already has decimal in current number
        }
      }

      setExpression(newValue);
      return newValue;
    });
  };

  const handleOperationPress = (nextOperation: string): void => {
    // Notify that typing has started
    if (onTypingStart) {
      onTypingStart();
    }

    if (((inputValue === '' || inputValue === '0')) && [OPERATOR_SYMBOLS.PLUS, OPERATOR_SYMBOLS.MULTIPLY, OPERATOR_SYMBOLS.DIVIDE].includes(nextOperation)) {
      return;
    }

    // Special handling for minus: if no input value or input is empty, make it negative
    if (nextOperation === OPERATOR_SYMBOLS.MINUS && (inputValue === '' || inputValue === '0')) {
      setInputValue('-');
      return;
    }

    // If input starts with just '-', convert to negative number
    if (nextOperation === OPERATOR_SYMBOLS.MINUS && inputValue === '-') {
      return; // Do nothing, already have minus sign
    }

    // Check if current input already contains an expression that can be calculated
    if (OPERATORS.some(operator => inputValue.includes(operator))) {
      // Calculate the current expression and use the result for the next operation
      const result = evaluateExpression(inputValue);
      const newExpression = `${result} ${nextOperation} `;
      setInputValue(newExpression);
      setExpression(newExpression);
      setPreviousValue(result);
      setOperation(nextOperation);
    } else {
      // Simple number, just add the operator
      const newExpression = `${inputValue} ${nextOperation} `;
      setInputValue(newExpression);
      setExpression(newExpression);
      setPreviousValue(parseFloat(inputValue) || 0);
      setOperation(nextOperation);
    }
  };

  const handleBackspace = (): void => {
    if (inputValue.length > 1) {
      const newValue = inputValue.slice(0, -1);
      setInputValue(newValue);
      if (previousValue !== null && operation) {
        setExpression(`${previousValue} ${operation} ${newValue}`);
      }
    } else {
      setInputValue('');
      if (previousValue !== null && operation) {
        setExpression(`${previousValue} ${operation}`);
      }
    }
  };

  const handleClear = (): void => {
    setInputValue('');
    setPreviousValue(null);
    setOperation(null);
    setExpression('');
  };

  const handleTripleZero = (): void => {
    // Notify that typing has started
    if (onTypingStart) {
      onTypingStart();
    }

    setInputValue(prevInputValue => {
      let newValue;

      // If marked for replacement (selectOnFocus), replace entire value
      if (shouldReplaceOnNextInput) {
        clearSelection();
        newValue = '000';
        setExpression(newValue);
        setPreviousValue(null);
        setOperation(null);
        return newValue;
      }

      if (prevInputValue === '0' || prevInputValue === '') {
        newValue = '000';
      } else if (prevInputValue.endsWith(' ')) {
        // Starting after an operator
        newValue = prevInputValue + '000';
      } else {
        newValue = prevInputValue + '000';
      }

      if (newValue.length <= 50) {
        setExpression(newValue);
        return newValue;
      }
      return prevInputValue;
    });
  };

  const calculateResult = (): string => {
    if (OPERATORS.some(operator => inputValue.includes(operator))) {
      const result = evaluateExpression(inputValue);
      return String(result);
    }

    return inputValue;
  };

  const handleEquals = (): void => {
    if (OPERATORS.some(operator => inputValue.includes(operator))) {
      const result = evaluateExpression(inputValue);
      setInputValue(String(result));
      setPreviousValue(null);
      setOperation(null);
      setExpression(String(result));
    }
  };

  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  return {
    inputValue,
    setInputValue,
    operation,
    previousValue,
    expression,
    isValueSelected,
    calculateResult,
    handleNumberPress,
    handleDecimal,
    handleOperationPress,
    handleEquals,
    handleBackspace,
    handleClear,
    handleTripleZero,
    markForReplacement,
    clearSelection,
  };
}; 